/* eslint-disable no-useless-escape */

import {
  forwardRef,
  type ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { InputMask } from "@react-input/mask";
import { useTranslation } from "react-i18next";

import { isProduct, ProductId } from "multitenancy/product";

import { useAppSelector } from "store/rootReducer";

import { HearingCustomFields } from "features/forms/HearingCustomFields";

import {
  Container,
  ContainerBody,
  ContainerInputs,
} from "pages/app/StyleCSS/bodyCreateEdit";

import { CheckBoxConfidential } from "components/CheckBoxConfidetial";
import DateInput from "components/DateInput";
import { DropdownGlobal } from "components/DropdownGlobal";
import { InputGeneral } from "components/Input";
import Multiline from "components/Multiline";
import TimeInput from "components/TimeInput";

import { IHearingCustomField } from "services/hearingCustomField";
import { HearingRoleService } from "services/hearingRole";
import { HearingSourceService } from "services/hearingSource";

import { applyMask, buildMaskConfig } from "utils/mask";

import { getRecordModeList } from "../constants";

const MaskedInputGeneral = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof InputGeneral>
>((inputProps, ref) => <InputGeneral {...inputProps} inputRef={ref} />);

MaskedInputGeneral.displayName = "MaskedInputGeneral";

const CONFIDENTIALITY_LEVEL_IDS = [0, 1, 2, 3, 4, 5];

type IErrorsFields = {
  nameAudience: boolean;
  hours: boolean;
  date: boolean;
  audienceType: boolean;
  recordMode: boolean;
};

interface ICreateAudience {
  valueEdit?: string;
  onChangeName: (e: string) => unknown;
  onChangeHours: (e: number) => unknown;
  onChangeDate: (e: string) => unknown;
  active: boolean;
  onChangeSwitch?: (e: boolean) => unknown;
  errorFields: IErrorsFields;
  date: any;
  time: any;
  confidentialityLevel: number;
  setConfidentialityLevel: (confidentialityLevel: number) => unknown;
  disabledConfidentialityLevel?: boolean;
  isClassified: boolean;
  setIsClassified: (isClassified: boolean) => unknown;
  setProcedureTypeId: (e: number) => unknown;
  procedureNumber?: string;
  editTypeSelected?: any;
  setObjectType?: any;
  hearingSourceId?: number | null;
  setHearingSourceId?: (id: number | null) => void;
  audience?: any;
  createAudienceMask: string;
  isEdit: boolean;
  recordModeSelected?: any;
  setRecordMode: (e: string) => unknown;
  audienceStatus?: string;
  onChangeDescription: (e: string) => unknown;
  audienceDescription?: string;
  customFields?: IHearingCustomField[];
  customFieldValues?: Record<number, string | boolean>;
  showCustomFieldErrors?: boolean;
  onCustomFieldChange?: (fieldId: number, value: string | boolean) => void;
}

const CreateAudience = (props: ICreateAudience) => {
  const { t, i18n } = useTranslation();
  const {
    audience,
    createAudienceMask,
    customFieldValues,
    customFields,
    isEdit,
    audienceStatus,
    onChangeName,
    procedureNumber,
    setObjectType,
  } = props;
  const [audienceTypeList, setAudienceTypeList] = useState<any>([]);
  const [hearingSourceList, setHearingSourceList] = useState<any[]>([]);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const procedureNumberRef = useRef<HTMLInputElement>(null);
  const hasCustomFields = Boolean(customFields?.length);

  const { user } = useAppSelector((state) => state.user);
  const isInquerito = isProduct(ProductId.Inquerito);
  const isCartorio = isProduct(ProductId.Cartorio);

  const disabledRecordMode = useMemo(() => {
    return isEdit && audienceStatus === "E";
  }, [audienceStatus, isEdit]);

  const maskConfig = useMemo(
    () => buildMaskConfig(createAudienceMask),
    [createAudienceMask],
  );

  const linkedProcedureFields = useMemo(() => {
    if (!customFields) return [];

    return customFields
      .filter((field) => field.linkedProperty === "ProcedureNumber")
      .sort((a, b) => a.index - b.index);
  }, [customFields]);

  const hasProcedureNumberLinked = linkedProcedureFields.length > 0;

  useEffect(() => {
    (async () => {
      if (user) {
        const response = await HearingRoleService.getAll(user?.customerId, 1);

        const getHearingType = response?.data?.data?.find(
          (type: any) => type?.id === audience?.hearingTypeId,
        );

        setObjectType?.(getHearingType);
        const onlyActives = response?.data?.data?.filter((e: any) => e.active);
        setAudienceTypeList(onlyActives);
      }
    })();
  }, [audience, setObjectType, user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = hasCustomFields
        ? descriptionRef.current
        : procedureNumberRef.current;
      input?.focus();
    }, 50);

    return () => clearTimeout(timer);
  }, [hasCustomFields]);

  useEffect(() => {
    if (!hasProcedureNumberLinked || !customFieldValues) return;

    const values = linkedProcedureFields.map((field) => {
      const value = customFieldValues?.[field.id];
      if (value === undefined || value === "") return "";

      const digitsOnly = String(value).replace(/\D/g, "");
      if (!digitsOnly) return "";

      const size = Math.max(0, field.size || 0);
      if (!size) return digitsOnly;

      const normalized =
        digitsOnly.length > size ? digitsOnly.slice(0, size) : digitsOnly;

      return normalized.padStart(size, "0");
    });

    const allEmpty = values.every((value) => value === "");
    if (allEmpty) return;

    const composedValue = values.join("");
    const formattedValue = createAudienceMask
      ? applyMask(composedValue, createAudienceMask)
      : composedValue;

    if (formattedValue !== procedureNumber) {
      onChangeName(formattedValue);
    }
  }, [
    createAudienceMask,
    customFieldValues,
    hasProcedureNumberLinked,
    linkedProcedureFields,
    onChangeName,
    procedureNumber,
  ]);

  useEffect(() => {
    if (!isInquerito || !user?.customerId) return;
    HearingSourceService.getAll(user.customerId, 1).then((response: any) => {
      if (response?.status && response?.data?.data) {
        setHearingSourceList(response.data.data.filter((e: any) => e.active));
      }
    });
  }, [isInquerito, user?.customerId]);

  const selectedHearingSource =
    hearingSourceList.find((s: any) => s.id === props.hearingSourceId) ?? null;

  const getSelected = (typeObject: any) => {
    props.setProcedureTypeId(typeObject?.id);
    props.setObjectType(typeObject);
  };

  const getSelectedRecordMode = (typeObject: any) => {
    props.setRecordMode(typeObject?.id || "");
  };

  const getSelectedConfidentialityLevel = (typeObject: any) => {
    const level = Number(typeObject?.id ?? 0);
    props.setConfidentialityLevel(level);
  };

  const onChangeProcessValueDefault = (e: string) => {
    const partner = /[\d\.\/\-\'']/gi;

    if (!!partner.test(e[e.length - 1]) || e === "") {
      return props.onChangeName(e);
    }
  };

  const recordModeList = getRecordModeList();
  const confidentialityLevels = useMemo(
    () =>
      CONFIDENTIALITY_LEVEL_IDS.map((id) => ({
        id,
        name: t(`profiles.secrecyLevels.${id}.option`),
      })),
    [t],
  );
  const selectedRecordMode = recordModeList.find(
    (recordMode) => Number(recordMode.id) === Number(props.recordModeSelected),
  );
  const selectedConfidentialityLevel =
    confidentialityLevels.find(
      (level) => level.id === Number(props.confidentialityLevel),
    ) ?? confidentialityLevels[0];

  return (
    <Container>
      <ContainerBody>
        <ContainerInputs>
          {props.createAudienceMask ? (
            <InputMask
              ref={procedureNumberRef}
              component={MaskedInputGeneral}
              mask={maskConfig.mask}
              showMask
              replacement={maskConfig.replacement}
              error={props.errorFields.nameAudience}
              value={props.procedureNumber || ""}
              onChange={props.onChangeName}
              label={t("audiences.procedureNumber")}
              nativeRequired={false}
              maxLength={200}
              required={true}
              disabled={hasProcedureNumberLinked}
              onKeyPress={(e) =>
                e.key === "Enter" ? e.preventDefault() : null
              }
              helperText={
                props.createAudienceMask && maskConfig.expectedLength
                  ? t("audiences.procedureNumberHelper", {
                      length: maskConfig.expectedLength,
                    })
                  : undefined
              }
            />
          ) : (
            <InputGeneral
              inputRef={procedureNumberRef}
              error={props.errorFields.nameAudience}
              value={props.procedureNumber || ""}
              onChange={onChangeProcessValueDefault}
              label={t("audiences.procedureNumber")}
              maxLength={200}
              required={true}
              nativeRequired={false}
              disabled={hasProcedureNumberLinked}
              onKeyPress={(e) =>
                e.key === "Enter" ? e.preventDefault() : null
              }
            />
          )}
        </ContainerInputs>

        <ContainerInputs fullWidth>
          <Multiline
            ref={descriptionRef}
            label={t("audiences.description")}
            value={props.audienceDescription || ""}
            onChange={(e: any) => props.onChangeDescription(e)}
            name={"descriptionAudience"}
            height="auto"
            maxRows={5}
            maxLength={250}
          />
        </ContainerInputs>

        <ContainerInputs>
          <DateInput
            label={t("audiences.dateTime")}
            setValue={(e: any) => props.onChangeDate(e)}
            value={props.date}
            required={true}
            error={
              props.date === "Invalid Date" ||
              (props.errorFields.date && !props.date)
            }
          />
          <div style={{ paddingTop: "20px", width: "400px" }}>
            <TimeInput
              label={""}
              setValue={(e: any) => props.onChangeHours(e)}
              error={!props.time && props.errorFields.hours}
              required={false}
              value={props.time}
            />
          </div>
        </ContainerInputs>

        <ContainerInputs>
          <DropdownGlobal
            withoutMarginBottom
            width="100%"
            error={props.errorFields.audienceType}
            editDropdown={props.editTypeSelected}
            labelName={t("labels.types")}
            options={audienceTypeList}
            required={true}
            setSelected={getSelected}
            wantAnObject={true}
          />
        </ContainerInputs>

        {isInquerito && (
          <ContainerInputs>
            <DropdownGlobal
              withoutMarginBottom
              width="100%"
              editDropdown={selectedHearingSource}
              labelName={t("audiences.procedureSource")}
              options={hearingSourceList}
              setSelected={(o: any) =>
                props.setHearingSourceId?.(o?.id ?? null)
              }
              wantAnObject={true}
            />
          </ContainerInputs>
        )}

        <ContainerInputs>
          <DropdownGlobal
            withoutMarginBottom
            width="100%"
            error={props.errorFields.recordMode}
            editDropdown={selectedRecordMode}
            labelName={t("audiences.recordMode")}
            options={recordModeList}
            required={true}
            setSelected={getSelectedRecordMode}
            wantAnObject={true}
            disabled={disabledRecordMode}
          />
        </ContainerInputs>

        {isCartorio && (
          <ContainerInputs>
            <CheckBoxConfidential
              label={t("audiences.confidential")}
              value={Boolean(props.isClassified)}
              onChange={(value) => props.setIsClassified?.(value)}
            />
          </ContainerInputs>
        )}

        {!isCartorio && (
          <ContainerInputs>
            <DropdownGlobal
              key={`confidentiality-level-${selectedConfidentialityLevel.id}-${i18n.language}`}
              withoutMarginBottom
              width="100%"
              editDropdown={selectedConfidentialityLevel}
              labelName={t("profiles.secrecyLevel")}
              options={confidentialityLevels}
              setSelected={getSelectedConfidentialityLevel}
              wantAnObject={true}
              disabled={props.disabledConfidentialityLevel}
            />
          </ContainerInputs>
        )}

        {props.customFields &&
          props.customFields.length > 0 &&
          props.customFieldValues &&
          props.onCustomFieldChange && (
            <ContainerInputs fullWidth>
              <HearingCustomFields
                showErrors={props.showCustomFieldErrors}
                fields={props.customFields}
                values={props.customFieldValues}
                onChange={props.onCustomFieldChange}
              />
            </ContainerInputs>
          )}
      </ContainerBody>
    </Container>
  );
};

export default CreateAudience;
